import { ComponentStyleConfig } from '@chakra-ui/react';
import { rgba } from 'polished';

import { COLORS } from 'theme';

const DatePickerTheme: ComponentStyleConfig = {
  baseStyle: () => ({
    '.react-datepicker': {
      fontFamily: 'unset',
      fontSize: '0.9rem',
      position: 'absolute',
      borderRadius: '12px',
      border: 'none',
      boxShadow:
        '0px 0px 0px 0.5px rgba(18, 21, 26, 0.08), 0px 2px 2px rgba(18, 21, 26, 0.04), 0px 4px 8px -2px rgba(18, 21, 26, 0.08)',
      backgroundColor: 'silver.lightest',
      zIndex: 1,
      marginTop: '8px',
      _dark: {
        boxShadow:
          '0px 0px 0px 0.5px rgba(18, 21, 26, 0.08), 0px 2px 2px rgba(18, 21, 26, 0.04), 0px 4px 8px -2px rgba(18, 21, 26, 0.08)',
        backgroundColor: 'black.lightest'
      }
    },
    '.react-datepicker__month-container': {
      display: 'flex',
      flexDirection: 'column'
    },
    '.react-datepicker__header': {
      backgroundColor: 'inherit',
      padding: '16px 16px 0 16px',
      borderBottom: 'none'
    },
    '.react-datepicker__header--custom': {
      order: 1,
      fontSize: '14px',
      fontWeight: '500',
      color: 'black.lighter',
      _dark: {
        color: 'silver.light'
      }
    },
    '.react-datepicker__month': {
      order: 2,
      padding: '6px 16px 16px 16px',
      margin: '0'
    },
    '.react-datepicker__day': {
      color: 'black.lighter',
      fontSize: '14px',
      width: '32px',
      height: '32px',
      margin: '2px',
      lineHeight: '31px',
      borderRadius: '8px',
      cursor: 'pointer',
      '&[aria-disabled=true]': {
        fontWeight: '400',
        color: 'grey.light',
        cursor: 'default'
      },
      _dark: {
        color: 'silver.light',
        '&[aria-disabled=true]': {
          color: 'grey.lighter'
        }
      },
      '&:focus-visible': {
        outline: 'none'
      },
      '&.react-datepicker__day--keyboard-selected': {
        outline: 'none',
        backgroundColor: 'inherit'
      },
      '&.day--is-selected': {
        background: 'blue.base',
        color: 'silver.lightest',
        fontWeight: '500',
        '&.react-datepicker__day--today': {
          '&::after': {
            backgroundColor: 'silver.lightest'
          }
        }
      },
      _hover: {
        '&:not([aria-disabled=true]):not([class*="react-datepicker__day--selecting-range-"]):not([class*="react-datepicker__day--range"])': {
          backgroundColor: rgba(COLORS.black.base, 0.04),
          _dark: {
            backgroundColor: rgba(COLORS.silver.lightest, 0.08)
          },
          '&.react-datepicker__day--selected': {
            color: 'silver.lightest',
            backgroundColor: 'blue.dark',
            _dark: {
              color: 'silver.lightest',
              backgroundColor: 'blue.dark'
            }
          }
        }
      },
      _focus: {
        boxShadow: `0px 0px 0px 1px ${COLORS.silver.lightest}, 0px 0px 0px 4px ${COLORS.blue.lighter}`,
        _dark: {
          boxShadow: `0px 0px 0px 1px ${COLORS.black.base}, 0px 0px 0px 4px ${COLORS.blue.lighter}`
        }
      }
    },
    '.react-datepicker__day-names': {
      margin: '0',
      height: '14px'
    },
    '.react-datepicker__day-name': {
      color: 'grey.base',
      fontWeight: '500',
      fontSize: '10px',
      lineHeight: '10px',
      height: 'min-content',
      width: '32px',
      margin: '0 2px',
      ':first-of-type': {
        marginLeft: '0'
      },
      ':last-of-type': {
        marginRight: '0'
      },
      _dark: {
        color: 'grey.lightest'
      }
    },
    '.react-datepicker__day--selected': {
      background: 'blue.base',
      fontWeight: '500',
      color: 'silver.lightest'
    },
    '.react-datepicker__day--in-selecting-range,\n.react-datepicker__day--in-range': {
      background: 'inherit',
      fontWeight: '400',
      color: 'black.lighter',
      _dark: {
        color: 'silver.light'
      }
    },
    '.react-datepicker__day--selected[aria-disabled=true]': {
      color: 'grey.light',
      background: 'inherit',
      fontWeight: '400',
      _dark: {
        color: 'grey.lighter'
      }
    },
    '.react-datepicker__day--outside-month': {
      visibility: 'hidden'
    },
    '.react-datepicker__day--today': {
      fontWeight: '500',
      position: 'relative',

      '&::after': {
        content: '""',
        width: '4px',
        height: '4px',
        borderRadius: '50%',
        background: 'black.lighter',
        position: 'absolute',
        bottom: '3px',
        left: '14px'
      },
      _dark: {
        '&::after': {
          background: 'silver.lighter'
        }
      }
    },
    '.react-datepicker__day--today.react-datepicker__day--selected::after': {
      backgroundColor: 'silver.lighter'
    },
    '.react-datepicker__day--today[aria-disabled=true]::after': {
      background: 'grey.light',
      _dark: {
        background: 'grey.lighter'
      }
    },
    '.react-datepicker__input-time-container': {
      margin: 0,
      lineHeight: 0
    },
    '.react-datepicker-time__input-container': {
      width: '100%',
      display: 'unset'
    },
    '.react-datepicker-time__input': {
      width: '100%'
    },
    '.react-datepicker-time__input-container .react-datepicker-time__input': {
      margin: 0,
      display: 'unset'
    },
    '.react-datepicker__input-time-container .react-datepicker-time__caption': {
      display: 'none'
    },
    '.react-datepicker__day.react-datepicker__day--in-range': {
      backgroundColor: 'blue.lightest',
      color: 'black.lighter',
      fontWeight: '400',
      margin: '2px 0',
      padding: '0 2px',
      width: '36px',
      borderRadius: '0',
      _dark: {
        backgroundColor: 'blue.darkest',
        color: 'silver.lightest'
      },
      _hover: {
        '&:not(.react-datepicker__day--range-start):not(.react-datepicker__day--range-end)': {
          backgroundColor: rgba(18, 21, 26, 0.04),
          padding: '0',
          borderRadius: '8px',
          position: 'relative',
          '::before': {
            content: "''",
            width: '100%',
            height: '100%',
            background: 'blue.lightest',
            position: 'absolute',
            left: 0,
            zIndex: '-1'
          },
          _dark: {
            background: rgba(COLORS.black.base, 0.08),
            '::before': {
              background: 'blue.darkest'
            }
          }
        }
      },
      _focus: {
        position: 'relative',
        '&:not(.react-datepicker__day--range-start):not(.react-datepicker__day--range-end)': {
          borderRadius: '8px',
          padding: '0',
          width: '32px',
          margin: '0 2px',
          '&.react-datepicker__day--today::after': {
            left: '14px',
            _dark: {
              '::before': {
                background: 'blue.darkest'
              }
            }
          },
          '::before': {
            content: '""',
            height: '100%',
            background: 'blue.lightest',
            position: 'absolute',
            left: 0,
            zIndex: '-1',
            width: '36px'
          },
          _dark: {
            '::before': {
              background: 'blue.darkest'
            }
          }
        }
      },
      '&.react-datepicker__day--today::after': {
        left: '16px'
      },
      '&.react-datepicker__day--today': {
        fontWeight: 500
      }
    },
    '.react-datepicker__day--in-selecting-range:not(.react-datepicker__day--selecting-range-start):not(.react-datepicker__day--selecting-range-end)': {
      '+ .react-datepicker__day--in-selecting-range, &:first-of-type': {
        backgroundColor: 'blue.lightest',
        _dark: {
          backgroundColor: 'blue.darkest'
        }
      },
      color: 'black.lighter',
      fontWeight: '400',
      margin: '2px 0',
      padding: '0 2px',
      width: '36px',
      borderRadius: '0',
      _dark: {
        color: 'silver.lightest'
      },
      _hover: {
        backgroundColor: rgba(18, 21, 26, 0.04),
        padding: '0',
        borderRadius: '8px',
        position: 'relative',
        '::before': {
          content: "''",
          width: '100%',
          height: '100%',
          background: 'blue.lightest',
          borderRadius: '0px 8px 8px 0px',
          position: 'absolute',
          left: 0,
          zIndex: '-1'
        },
        _dark: {
          background: rgba(COLORS.black.base, 0.08),
          '::before': {
            background: 'blue.darkest'
          }
        }
      },
      _focus: {
        position: 'relative',
        '&:not(.react-datepicker__day--range-start):not(.react-datepicker__day--range-end)': {
          borderRadius: '8px',
          padding: '0',
          width: '32px',
          margin: '0 2px',
          '&.react-datepicker__day--today::after': {
            left: '14px'
          },
          '::before': {
            content: '""',
            height: '100%',
            background: 'blue.lightest',
            borderRadius: '0px 8px 8px 0px',
            position: 'absolute',
            left: 0,
            zIndex: '-1',
            width: '36px'
          },
          _dark: {
            '::before': {
              background: 'blue.darkest'
            }
          }
        }
      },
      '&.react-datepicker__day--today': {
        fontWeight: '500',
        '&::after': {
          left: '16px'
        }
      }
    },
    '.react-datepicker__day.react-datepicker__day--selecting-range-start': {
      backgroundColor: 'blue.base',
      color: 'silver.lightest',
      fontWeight: '500',
      borderRadius: '8px 0px 0px 8px',
      marginRight: '0',
      '&.react-datepicker__day--today::after': {
        backgroundColor: 'silver.lightest'
      },
      ' + .react-datepicker__day--in-selecting-range': {
        position: 'relative',
        '::before': {
          content: "''",
          width: '100%',
          height: '100%',
          background: 'blue.lightest',
          position: 'absolute',
          left: 0,
          zIndex: '-1'
        }
      },
      _dark: {
        ' + .react-datepicker__day--in-selecting-range': {
          position: 'relative',
          '::before': {
            backgroundColor: 'blue.darkest'
          }
        }
      }
    },
    '.react-datepicker__day.react-datepicker__day--range-start,.react-datepicker__day.react-datepicker__day--selecting-range-start': {
      backgroundColor: 'blue.base',
      borderRight: `2px solid ${COLORS.blue.lightest}`,
      color: 'silver.lightest',
      fontWeight: '500',
      marginLeft: '2px',
      borderRadius: '8px 0px 0px 8px',
      width: '34px',
      _dark: {
        backgroundColor: 'blue.base',
        borderRight: `2px solid ${COLORS.blue.darkest}`
      },
      _hover: {
        backgroundColor: 'blue.dark',
        _dark: {
          backgroundColor: 'blue.dark'
        }
      },
      _focus: {
        borderRight: '0',
        marginLeft: '1px',
        marginRight: '1px',
        zIndex: '1',
        _dark: {
          borderRight: '0'
        },
        '&.react-datepicker__day--today::after': {
          left: '15px'
        }
      },
      '&.react-datepicker__day--today::after': {
        backgroundColor: 'silver.lightest',
        left: '14px'
      }
    },
    '.react-datepicker__day.react-datepicker__day--range-end': {
      backgroundColor: 'blue.base',
      borderLeft: `2px solid ${COLORS.blue.lightest}`,
      color: 'silver.lightest',
      fontWeight: '500',
      marginRight: '2px',
      borderRadius: '0px 8px 8px 0px',
      width: '34px',
      _dark: {
        backgroundColor: 'blue.base',
        borderLeft: `2px solid ${COLORS.blue.darkest}`
      },
      _hover: {
        backgroundColor: 'blue.dark',
        _dark: {
          backgroundColor: 'blue.dark'
        }
      },
      _focus: {
        borderLeft: '0',
        marginLeft: '1px',
        marginRight: '1px',
        _dark: {
          borderLeft: '0'
        },
        '&.react-datepicker__day--today::after': {
          left: '15px'
        }
      },
      '&.react-datepicker__day--today::after': {
        backgroundColor: 'silver.lightest',
        left: '14px'
      }
    },
    '.react-datepicker__day--range-start.react-datepicker__day--range-end.react-datepicker__day--in-range': {
      borderRadius: '8px',
      borderWidth: 0,
      width: '32px',
      padding: '0px',
      _focus: {
        margin: '2px',
        width: '32px',
        '&.react-datepicker__day--today::after': {
          left: '14px'
        }
      }
    },
    '.react-datepicker__header--custom .chakra-select__icon-wrapper': {
      right: '0'
    },
    calendarFakeSelect: {
      paddingRight: '28px',
      paddingTop: '7px',
      width: 'min-content',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: '100px'
    },
    calendarSelect: {
      paddingRight: '24px'
    },
    calendarPreviousMonthButton: {
      marginLeft: '4px',
      marginRight: '4px',
      transform: 'rotate(90deg)'
    },
    calendarNextMonthButton: {
      transform: 'rotate(-90deg)'
    },
    footerContainer: {
      padding: '0 16px 16px 16px',
      display: 'flex',
      justifyContent: 'end'
    },
    inputButtonContainer: {
      display: 'flex',
      padding: '0',
      background: 'silver.lighter',
      borderColor: 'grey.lightest',
      color: 'black.lighter',
      _dark: {
        backgroundColor: 'black.light',
        borderColor: 'black.lightest',
        color: 'silver.light'
      }
    },
    inputsContainer: {
      display: 'flex',
      width: '100%'
    },
    rangeInputStart: {
      paddingRight: '0',
      width: '108px',
      borderRadius: '0',
      color: 'black.lighter',
      _placeholder: {
        color: 'grey.light'
      },
      _dark: {
        color: 'silver.light',
        _placeholder: {
          color: 'grey.lighter'
        }
      }
    },
    rangeInputEnd: {
      paddingLeft: '0',
      width: '108px',
      borderRadius: '0',
      color: 'black.lighter',
      _placeholder: {
        color: 'grey.light'
      },
      _dark: {
        color: 'silver.light',
        _placeholder: {
          color: 'grey.lighter'
        }
      }
    },
    rangeInputDivider: {
      paddingLeft: '8px',
      paddingRight: '16px',
      color: 'black.lighter',
      _dark: {
        color: 'silver.light'
      }
    }
  })
};

export default DatePickerTheme;
